import { Injectable } from '@angular/core';
import { ChangelogService, ChangelogVersion } from 'app/core/atualizacoes/changelog.service';
import { SolicitacoesService } from 'app/core/atualizacoes/solicitacoes.service';
import { Solicitacao, SolicitacoesMe, STATUS_INFO } from 'app/core/atualizacoes/solicitacoes.types';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { catchError, forkJoin, map, Observable, of, ReplaySubject, take, tap } from 'rxjs';

/** Quantas versões do changelog viram notificação. */
const MAX_VERSIONS = 5;
const READ_KEY = 'aura.notifications.read';
const REMOVED_KEY = 'aura.notifications.removed';
/** Mudança de status de solicitação vira aviso por este tempo. */
const DIAS_AVISO_SOLICITACAO = 30;

/**
 * Notificações do sino: cada versão do CHANGELOG.md e, das solicitações dos
 * clientes, as mudanças de status das minhas (aprovada, em andamento, concluída,
 * recusada) e, para o admin Automining, as que chegaram para análise.
 * Lida/removida fica guardado no navegador do usuário.
 */
@Injectable({
	providedIn: 'root'
})
export class NotificationsService {
	private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
	private _current: Notification[] = [];

	constructor(
		private _changelogService: ChangelogService,
		private _solicitacoesService: SolicitacoesService
	) {
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	get notifications$(): Observable<Notification[]> {
		return this._notifications.asObservable();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	getAll(): Observable<Notification[]> {
		// Roda no resolver inicial do app: nenhuma das fontes pode derrubar a carga.
		return forkJoin([
			this._changelogService.getVersions().pipe(take(1), catchError(() => of([] as ChangelogVersion[]))),
			this._solicitacoesNotifications(),
		]).pipe(
			map(([versions, solicitacoes]) => {
				const read = this._load(READ_KEY);
				const removed = this._load(REMOVED_KEY);
				return [...solicitacoes, ...versions.slice(0, MAX_VERSIONS).map((v) => this._toNotification(v))]
					.filter((n) => !removed.includes(n.id))
					.sort((a, b) => b.time.localeCompare(a.time))
					.map((n) => ({ ...n, read: read.includes(n.id) }));
			}),
			tap((notifications) => this._emit(notifications))
		);
	}

	create(notification: Notification): Observable<Notification> {
		this._emit([notification, ...this._current]);
		return of(notification);
	}

	update(id: string, notification: Notification): Observable<Notification> {
		this._setRead(id, notification.read);
		this._emit(this._current.map((n) => (n.id === id ? { ...n, ...notification } : n)));
		return of(notification);
	}

	delete(id: string): Observable<boolean> {
		this._save(REMOVED_KEY, [...new Set([...this._load(REMOVED_KEY), id])]);
		this._emit(this._current.filter((n) => n.id !== id));
		return of(true);
	}

	markAllAsRead(): Observable<boolean> {
		this._save(READ_KEY, [...new Set([...this._load(READ_KEY), ...this._current.map((n) => n.id)])]);
		this._emit(this._current.map((n) => ({ ...n, read: true })));
		return of(true);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------

	private _toNotification(v: ChangelogVersion): Notification {
		const count = (type: string): number =>
			v.sections.filter((s) => s.type.toLowerCase().startsWith(type)).reduce((t, s) => t + s.items.length, 0);
		const resumo = [
			[count('adicion'), 'novidade', 'novidades'],
			[count('alter'), 'alteração', 'alterações'],
			[count('corrig'), 'correção', 'correções'],
			[count('remov'), 'remoção', 'remoções'],
		]
			.filter(([n]) => n > 0)
			.map(([n, um, varios]) => `${n} ${n === 1 ? um : varios}`)
			.join(' · ');
		const areas = [...new Set(v.sections.flatMap((s) => s.items.map((i) => i.area).filter(Boolean)))];

		return {
			id: `versao-${v.version}`,
			icon: 'heroicons_outline:sparkles',
			title: `Versão ${v.version}`,
			description: [resumo, areas.slice(0, 3).join(', ') + (areas.length > 3 ? '…' : '')].filter(Boolean).join(' — '),
			// meio-dia evita que a data mude de dia pelo fuso
			time: v.date ? `${v.date}T12:00:00` : new Date().toISOString(),
			link: '/apps/atualizacoes',
			useRouter: true,
			read: false,
		};
	}

	private _solicitacoesNotifications(): Observable<Notification[]> {
		return forkJoin([this._solicitacoesService.me(), this._solicitacoesService.list()]).pipe(
			map(([me, lista]) => this._solicitacoesToNotifications(me, lista)),
			catchError(() => of([]))
		);
	}

	private _solicitacoesToNotifications(me: SolicitacoesMe, lista: Solicitacao[]): Notification[] {
		const limite = Date.now() - DIAS_AVISO_SOLICITACAO * 24 * 60 * 60 * 1000;
		const recentes = lista.filter((s) => new Date(s.statusChangedAt).getTime() >= limite);
		const link = { link: '/apps/atualizacoes', queryParams: { aba: 'solicitacoes' }, useRouter: true, read: false };

		const minhas = recentes
			.filter((s) => s.requesterEmail === me.email && s.status !== 'EM_ANALISE')
			.map((s): Notification => ({
				...link,
				id: `solicitacao-${s.id}-${s.status}`,
				icon: 'heroicons_outline:chat-alt-2',
				title: `Solicitação #${s.number}: ${STATUS_INFO[s.status].label.toLowerCase()}`,
				description: s.title,
				time: s.statusChangedAt,
			}));

		const paraAnalisar = me.isAdmin
			? recentes
				.filter((s) => s.status === 'EM_ANALISE')
				.map((s): Notification => ({
					...link,
					id: `solicitacao-${s.id}-EM_ANALISE`,
					icon: 'heroicons_outline:inbox',
					title: `Nova solicitação #${s.number}`,
					description: `${s.title} — ${s.requesterName || s.requesterEmail}`,
					time: s.statusChangedAt,
				}))
			: [];

		return [...paraAnalisar, ...minhas];
	}

	private _setRead(id: string, read: boolean): void {
		const ids = this._load(READ_KEY).filter((i) => i !== id);
		this._save(READ_KEY, read ? [...ids, id] : ids);
	}

	private _emit(notifications: Notification[]): void {
		this._current = notifications;
		this._notifications.next(notifications);
	}

	private _load(key: string): string[] {
		try {
			return JSON.parse(localStorage.getItem(key) ?? '[]');
		} catch {
			return [];
		}
	}

	private _save(key: string, ids: string[]): void {
		try {
			localStorage.setItem(key, JSON.stringify(ids));
		} catch {
			// navegador sem storage: segue só em memória
		}
	}
}
